import type { CustomerPortalUserRecord } from "./customerInvitation";

export type AuthenticatorAssuranceLevel = "aal1" | "aal2" | "aal3" | string | null;

export type TotpFactor = {
  id: string;
  factor_type?: string;
  factorType?: string;
  status?: string;
  friendly_name?: string | null;
  friendlyName?: string | null;
};

export type MfaFactorList =
  | {
      all?: TotpFactor[];
      totp?: TotpFactor[];
    }
  | TotpFactor[]
  | null
  | undefined;

export type TotpSetupPlan =
  | {
      action: "enroll_new_factor";
      verifiedFactor: null;
      unverifiedFactorIds: [];
    }
  | {
      action: "replace_unverified_factors";
      verifiedFactor: null;
      unverifiedFactorIds: string[];
    }
  | {
      action: "use_verified_factor";
      verifiedFactor: TotpFactor;
      unverifiedFactorIds: string[];
    };

export function isAal2(level: AuthenticatorAssuranceLevel) {
  return level === "aal2";
}

export function isValidTotpCode(value: string) {
  return /^\d{6}$/.test(value.trim());
}

function hasTotpType(factor: TotpFactor) {
  const factorType = factor.factor_type || factor.factorType;
  return !factorType || factorType === "totp";
}

export function isExplicitTotpFactor(factor: TotpFactor) {
  return (factor.factor_type || factor.factorType) === "totp";
}

export function getTotpFactors(factors: MfaFactorList): TotpFactor[] {
  if (!factors) return [];

  if (Array.isArray(factors)) {
    return factors.filter(hasTotpType);
  }

  if (Array.isArray(factors.all)) {
    return factors.all.filter(hasTotpType);
  }

  if (Array.isArray(factors.totp)) {
    return factors.totp;
  }

  return [];
}

export function selectVerifiedTotpFactor(factors: MfaFactorList) {
  return getTotpFactors(factors).find((factor) => factor.status === "verified") || null;
}

export function selectUnverifiedTotpFactors(factors: MfaFactorList) {
  return getTotpFactors(factors).filter((factor) => factor.status !== "verified");
}

export function getTotpSetupPlan(factors: MfaFactorList): TotpSetupPlan {
  const verifiedFactor = selectVerifiedTotpFactor(factors);
  const unverifiedFactorIds = selectUnverifiedTotpFactors(factors).map(
    (factor) => factor.id
  );

  if (verifiedFactor) {
    return {
      action: "use_verified_factor",
      verifiedFactor,
      unverifiedFactorIds,
    };
  }

  if (unverifiedFactorIds.length > 0) {
    return {
      action: "replace_unverified_factors",
      verifiedFactor: null,
      unverifiedFactorIds,
    };
  }

  return {
    action: "enroll_new_factor",
    verifiedFactor: null,
    unverifiedFactorIds: [],
  };
}

export function getQrImageSource(qrCode: string) {
  const trimmed = qrCode.trim();

  if (trimmed.startsWith("<svg")) {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(trimmed)}`;
  }

  return qrCode;
}

export function canUseMfaSetup(portalUser: CustomerPortalUserRecord | null | undefined) {
  return Boolean(
    portalUser &&
      !portalUser.disabled_at &&
      ["mfa_pending", "active"].includes(portalUser.activation_status) &&
      portalUser.auth_user_id &&
      portalUser.portal_login_email &&
      portalUser.username
  );
}

export function canUseMfaVerify(portalUser: CustomerPortalUserRecord | null | undefined) {
  return Boolean(
    portalUser &&
      !portalUser.disabled_at &&
      portalUser.activation_status === "active" &&
      portalUser.auth_user_id &&
      portalUser.portal_login_email &&
      portalUser.username &&
      portalUser.mfa_enrolled_at
  );
}

export function canCompleteMfaEnrollment({
  currentLevel,
  portalUser,
}: {
  currentLevel: AuthenticatorAssuranceLevel;
  portalUser: CustomerPortalUserRecord | null | undefined;
}) {
  return canUseMfaSetup(portalUser) && isAal2(currentLevel);
}
